/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import {
  isTopNotePath,
  NOTE_PATH_SEPARATOR,
  splitNotePath,
  TOP_NOTE_PATH,
  type NoteTreeEntryInterface
} from '@hedgedoc/commons'

export { isTopNotePath, TOP_NOTE_PATH }

export interface NoteTreeFolder {
  /** The last segment of the path, shown as the folder name */
  name: string
  /** The full path of the folder, segments separated by slashes */
  path: string
  folders: NoteTreeFolder[]
  notes: NoteTreeEntryInterface[]
}

export interface NoteTree {
  /** Notes with the special path {@link TOP_NOTE_PATH}, listed above all folders */
  top: NoteTreeEntryInterface[]
  /** Top-level folders */
  folders: NoteTreeFolder[]
  /** Notes without a path */
  unfiled: NoteTreeEntryInterface[]
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/**
 * Returns the label to show for a note in the tree: its title or, if the note has no title, its alias.
 *
 * @param entry The note to get the label for
 * @return The label of the note
 */
export const getNoteLabel = (entry: NoteTreeEntryInterface): string => entry.title || entry.primaryAlias

const compareNotes = (a: NoteTreeEntryInterface, b: NoteTreeEntryInterface): number =>
  collator.compare(getNoteLabel(a), getNoteLabel(b)) || collator.compare(a.primaryAlias, b.primaryAlias)

const sortFolder = (folder: NoteTreeFolder): void => {
  folder.folders.sort((a, b) => collator.compare(a.name, b.name))
  folder.notes.sort(compareNotes)
  folder.folders.forEach(sortFolder)
}

/**
 * Builds the nested folder structure of the navigation tree from the flat list of notes.
 * Folders are sorted by name and notes by their label, both comparing numbers naturally (inci-2 before inci-10).
 *
 * @param entries The notes to put into the tree
 * @return The tree of folders, the notes listed above all folders and the notes without a path
 */
export const buildNoteTree = (entries: NoteTreeEntryInterface[]): NoteTree => {
  const root: NoteTreeFolder = { name: '', path: '', folders: [], notes: [] }
  const top: NoteTreeEntryInterface[] = []
  const unfiled: NoteTreeEntryInterface[] = []
  for (const entry of entries) {
    if (isTopNotePath(entry.path)) {
      top.push(entry)
      continue
    }
    const segments = splitNotePath(entry.path)
    if (segments.length === 0) {
      unfiled.push(entry)
      continue
    }
    let folder = root
    for (const segment of segments) {
      let child = folder.folders.find((candidate) => candidate.name === segment)
      if (child === undefined) {
        const path = folder.path ? `${folder.path}${NOTE_PATH_SEPARATOR}${segment}` : segment
        child = { name: segment, path, folders: [], notes: [] }
        folder.folders.push(child)
      }
      folder = child
    }
    folder.notes.push(entry)
  }
  sortFolder(root)
  top.sort(compareNotes)
  unfiled.sort(compareNotes)
  return { top, folders: root.folders, unfiled }
}

/**
 * Counts all notes in the folder, including those in sub folders.
 *
 * @param folder The folder to count the notes of
 * @return The number of notes
 */
export const countNotesInFolder = (folder: NoteTreeFolder): number =>
  folder.notes.length + folder.folders.reduce((sum, child) => sum + countNotesInFolder(child), 0)

/**
 * Collects all notes in the folder, including those in sub folders.
 *
 * @param folder The folder to collect the notes of
 * @return The list of notes
 */
export const collectNotesInFolder = (folder: NoteTreeFolder): NoteTreeEntryInterface[] => [
  ...folder.notes,
  ...folder.folders.flatMap(collectNotesInFolder)
]

/**
 * Returns the paths of all folders that contain the given path, including the path itself.
 * This is used to expand the folders leading to the currently opened note.
 *
 * @param path The path of a note
 * @return The paths of the folder and its ancestors, e.g. ['Incidents', 'Incidents/2026']
 */
export const getAncestorFolderPaths = (path: string): string[] => {
  const segments = splitNotePath(path)
  return segments.map((_, index) => segments.slice(0, index + 1).join(NOTE_PATH_SEPARATOR))
}

/**
 * Finds the folder with the given path in the tree. Folder names are compared ignoring case.
 *
 * @param tree The tree to search in
 * @param path The path of the folder
 * @return The folder or undefined if there is no folder with this path
 */
export const findFolder = (tree: NoteTree, path: string): NoteTreeFolder | undefined => {
  let folders = tree.folders
  let found: NoteTreeFolder | undefined = undefined
  for (const segment of splitNotePath(path)) {
    const lowercaseSegment = segment.toLowerCase()
    found = folders.find((folder) => folder.name.toLowerCase() === lowercaseSegment)
    if (found === undefined) {
      return undefined
    }
    folders = found.folders
  }
  return found
}
